import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import type { Block, Inline } from '@contentful/rich-text-types';
import { Brand, SitesToBrands } from '@snapchat/mw-common';
import { useContentfulImages } from '@snapchat/mw-contentful-client';
import { CarouselCardItem, CarouselV3 } from '@snapchat/snap-design-system-marketing';
import { type FC, useContext } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import { useContentfulQuery } from '../../hooks/useContentfulQuery';
import { useFormatDate } from '../../hooks/useFormatDate';
import { useParseMediaContainer } from '../../hooks/useParseMediaContainer';
import type { ContentfulSysProps } from '../../types/contentful';
import { combineImageSources } from '../../utils/combineImageSources';
import { SlugContext } from '../Slug/SlugContext';
import { type TileQueryResponse, tileCollectionAndRelatedContentQuery } from './query';
import { useRelatedContent } from './useRelatedContent';

// Limit the number of tiles displayed in the Related Content section
const tilesLimit = 10;

const defaultDateOptions: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  timeZone: 'UTC',
};

export const RelatedContent: FC<ContentfulSysProps> = props => {
  const { topics, slugId = '' } = useContext(SlugContext);
  const { getImageSources } = useContentfulImages();
  const { isRTL } = useContext(AppContext);
  const { formatDate } = useFormatDate();

  const topicsIds = topics?.map(topic => topic.topicId || '') ?? [];

  const { paginationData, isLoading } = useRelatedContent({
    page: 1,
    pageSize: tilesLimit,
    tags: topicsIds,
  });

  const { data: tilesData, loading: loadingTiles } = useContentfulQuery<
    TileQueryResponse,
    { ids: string[]; relatedContentId: string; slugId?: string }
  >(tileCollectionAndRelatedContentQuery, {
    variables: {
      ids: paginationData?.tiles?.map(tile => tile?.tileSysId) ?? [],
      relatedContentId: props.sys.id,
      slugId, // TODO: Revisit and skip this if slugId is indexed on elastic Tiles
    },
  });

  /*
  This is a global fallback image to display on a tile when the user doesn't provide an image for the gallery or tile.
  This image is in the global-components contentful space
  */
  const globalDefaultImage =
    'https://images.ctfassets.net/kp51zybwznx4/120IJvdruE5HeC9XBYjFiU/879b8f63bde0d27c0750ade94c4b1a39/Default_Gallery_Image.png';

  // this will always be a default image or undefined, so we can ignore video data
  const {
    media: { imageSource: defaultImageSource, imageAltText: defaultImageAltText },
    mobileMedia: { imageSource: defaultMobileImageSource },
  } = useParseMediaContainer(tilesData?.relatedContent?.defaultImage);

  const tilesCount = tilesData?.tileCollection?.items.length || 0;

  if (!isLoading && !loadingTiles && tilesCount > 0) {
    return (
      <CarouselV3 isRtl={isRTL}>
        {tilesData?.tileCollection?.items.map(tile => {
          const tileMediaType = tile.media?.media?.contentType;
          const tileHasVideo = tileMediaType?.startsWith('video/');
          const tileHasMedia = !!tileMediaType;

          // only use the default image if the tile does not have media
          const imgSrcs = combineImageSources({
            desktop: getImageSources(
              tileHasMedia ? tile.media?.media.url : defaultImageSource || globalDefaultImage
            ),
            mobile: getImageSources(
              tileHasMedia ? tile.media?.mobileMedia?.url : defaultMobileImageSource
            ),
          });

          const tileSlug = tile.slugReference?.slug || '';
          const tileTitle = tile.title?.json ? tile.title.json : '';
          const titleBody = tile.label?.json ? tile.label?.json : '';
          const tileDate = tile.date
            ? formatDate(tile.date, defaultDateOptions).replaceAll('/', '.')
            : ''; // Result example: 09.09.25

          return (
            <CarouselCardItem
              key={`${tile.__typename}-${tile.sys.id}`}
              title={documentToPlainTextString(tileTitle as Block | Inline)}
              body={documentToPlainTextString(titleBody as Block | Inline)}
              date={tileDate}
              brand={tile?.brand}
              url={tileSlug ? `/${tileSlug}` : undefined}
              imgSrcs={!tileHasVideo ? imgSrcs : undefined}
              imgAltText={tile.media?.media?.description || defaultImageAltText}
              videoSource={tileHasVideo ? tile.media?.media?.url : undefined}
              mobileVideoSource={tileHasVideo ? tile.media?.mobileMedia?.url : undefined}
              aspectRatio="9:16" // This value is set by design
              // Hide the link arrow for Snap-branded tiles
              hideLinkArrow={SitesToBrands[Config.site]?.includes(Brand.SNAP)}
              // CarouselCardItem relatedContent prop is used to apply the correct line clamp to the body text
              relatedContent
            />
          );
        })}
      </CarouselV3>
    );
  }

  return null;
};
